import { useCallback, useMemo, useRef, useState } from 'react';
import { useLastValueRef } from './use-last-value-ref';

export enum EUseAsyncStatus {
    FRESH = 1,
    PENDING = 2,
    FULFILLED = 4,
    REJECTED = 8,
}
type TExecute<_Result, _Param> = (() => PromiseLike<_Result>) &
    ((params: _Param) => PromiseLike<_Result>);

interface TResult<T, Params> {
    value: T | undefined;
    error: unknown;
    params: Params;
}

export interface TUseAsync<T, Params> extends TResult<T, Params> {
    execute: TExecute<T, Params>;
    status: EUseAsyncStatus;
    current: TResult<T, Params>;
    retry: TExecute<T, Params>;
    isFresh: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    isFailure: boolean;
    isCompleted: boolean;
}

/**
 * @example <caption>Quick start</caption>
 * <pre>
 * const [id, setId] = useState(1);
 * const loader = useAsync((id) => request(`/api/user/${id}`));
 * useEffect(() => {
 *   if (id) loader.execute(id);
 * }, [id]);
 * return <div>{loader.value}</div>
 * </pre>
 *
 * @example <caption>Get promise result</caption>
 * <pre>
 * // get value or error from latest completed promise
 * console.log(loader.value, loader.error, loader.params);
 * // get value or error from current promise
 * console.log(loader.current.value, loader.current.error, loader.current.params);
 * </pre>
 *
 * @example <caption>Get promise status</caption>
 * <pre>
 * loader.isFresh;
 * loader.isLoading;
 * loader.isSuccess;
 * loader.isFailure;
 * loader.isCompleted;
 * </pre>
 */
export function useAsync<T, Params = never>(fn: TExecute<T, Params>): TUseAsync<T, Params> {
    const [status, setStatus] = useState<EUseAsyncStatus>(EUseAsyncStatus.FRESH);
    const [result, setResult] = useState<TResult<T, Params>>();
    const pendingPromiseRef = useRef<PromiseLike<T>>();
    const pendingParamsRef = useRef<Params>();
    const rFn = useLastValueRef(fn);

    const execute = useCallback<TExecute<T, Params>>((params?: Params) => {
        const rawPromise = typeof params === 'undefined' ? rFn.current() : rFn.current(params);
        const p: PromiseLike<T> = rawPromise.then<T, T>(
            r =>
                pendingPromiseRef.current === p ? r : (pendingPromiseRef.current as PromiseLike<T>),
            e => (pendingPromiseRef.current === p ? Promise.reject(e) : pendingPromiseRef.current as PromiseLike<T>),
        );
        pendingPromiseRef.current = p;
        pendingParamsRef.current = params;
        setStatus(EUseAsyncStatus.PENDING);

        p.then(
            value => {
                if (pendingPromiseRef.current === p) {
                    setResult({
                        params: pendingParamsRef.current as Params,
                        value,
                        error: undefined,
                    });
                    setStatus(EUseAsyncStatus.FULFILLED);

                    pendingPromiseRef.current = undefined;
                    pendingParamsRef.current = undefined;
                }
            },
            error => {
                if (pendingPromiseRef.current === p) {
                    setResult({
                        params: pendingParamsRef.current as Params,
                        value: undefined,
                        error,
                    });
                    setStatus(EUseAsyncStatus.REJECTED);

                    pendingPromiseRef.current = undefined;
                    pendingParamsRef.current = undefined;
                }
            }
        );
        return p;
    }, [rFn]);

    const statusSugars = {
        isFresh: status === EUseAsyncStatus.FRESH,
        isLoading: status === EUseAsyncStatus.PENDING,
        isSuccess: status === EUseAsyncStatus.FULFILLED,
        isFailure: status === EUseAsyncStatus.REJECTED,
        isCompleted: status === EUseAsyncStatus.REJECTED || status === EUseAsyncStatus.FULFILLED,
    };

    const pendingResult = useMemo<TResult<T, Params>>(
        () => ({
            value: undefined,
            error: undefined,
            params: pendingParamsRef.current as Params,
        }),
        //eslint-disable-next-line
        [statusSugars.isLoading]
    );

    return {
        execute,
        status,
        ...pendingResult,
        ...result,
        current: statusSugars.isCompleted ? (result as TResult<T, Params>) : pendingResult,
        retry: execute,
        ...statusSugars,
    };
}
